import { Link } from "react-router";
import { useState, type ReactNode } from "react";
import { PLAN_LABEL, planWith } from "../../shared/market";
import { STATS_PERIODS, type SellerInsights, type SellerPerformance, type SellerStatsView, type StatsPeriod } from "../../shared/marketStats";
import { sellerPath } from "../../shared/sellerPaths";
import { LANGUAGE_LABEL } from "../../shared/variants";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { useAsync, type AsyncState } from "../lib/useAsync";
import { marketApi } from "./api";
import { Stars, useArs } from "./components";
import { Funnel, Prices, useDuration } from "./InsightsSection";
import { NeedsPush, Sources } from "./PerformanceSection";
import { ShowcaseOr, UpgradeLink } from "./Showcase";

const PERIOD_LABEL: Record<StatsPeriod, MessageKey> = { 30: "st.period30", 90: "st.period90", 0: "st.periodAll" };

const dayMonth = (iso: string, locale: "es" | "en") => new Date(`${iso}T12:00:00`).toLocaleDateString(locale === "es" ? "es-AR" : "en-US", { day: "numeric", month: "numeric" });
const monthName = (iso: string, locale: "es" | "en") => new Date(`${iso}T12:00:00`).toLocaleDateString(locale === "es" ? "es-AR" : "en-US", { month: "short", year: "2-digit" });
const listingName = (l: { name: string; setName: string | null; cardNumber: string | null }) => [l.name, l.setName, l.cardNumber && `#${l.cardNumber}`].filter(Boolean).join(" · ");

/** Estadísticas' sub-tabs: how the shop is doing, which cards work, and its prices today. */
export type StatsView = "resumen" | "cartas" | "precios";

/**
 * Mi tienda › Estadísticas, one component for its three sub-tabs, so the period picked and what's
 * loaded carry over from one to the other. Precios is all today's data: no period there. What a plan
 * lacks shows once per sub-tab, with one way to it (none on Resumen).
 */
export function StatsTab({ view, insights = false, performance = false, canFeature = false }: { view: StatsView; insights?: boolean; performance?: boolean; canFeature?: boolean }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState<StatsPeriod>(30);
  const [page, setPage] = useState(1);
  const stats = useAsync((signal) => marketApi.stats(period, signal), [period]);
  // EX and up only (a plan without them never asks); Rendimiento (VMAX) has nothing for Precios.
  const ins = useAsync((signal) => (insights ? marketApi.insights(period, page, signal) : Promise.resolve(null)), [insights, period, page]);
  const wantPerf = performance && view !== "precios";
  const perf = useAsync((signal) => (wantPerf ? marketApi.performance(period, signal) : Promise.resolve(null)), [wantPerf, period]);
  const pick = (p: StatsPeriod) => {
    setPeriod(p);
    setPage(1);
  };
  return (
    <div className="stats-tab stack">
      {view === "precios" ? (
        <p className="muted small">{t("st.pricesToday")}</p>
      ) : (
        <div className="stats-periods" role="group" aria-label={t("st.period")}>
          {STATS_PERIODS.map((p) => (
            <button key={p} type="button" className={p === period ? "btn btn-primary btn-sm" : "btn btn-outline btn-sm"} aria-pressed={p === period} onClick={() => pick(p)}>
              {t(PERIOD_LABEL[p])}
            </button>
          ))}
        </div>
      )}
      {stats.error ? (
        <ErrorMessage onRetry={stats.reload} />
      ) : !stats.data ? (
        <Loading />
      ) : view === "resumen" ? (
        <Summary s={stats.data} ins={insights ? ins : null} perf={performance ? perf : null} />
      ) : view === "cartas" ? (
        <Cards s={stats.data} ins={insights ? ins : null} perf={performance ? perf : null} page={page} onPage={setPage} canFeature={canFeature} />
      ) : (
        <PricesView s={stats.data} ins={insights ? ins : null} />
      )}
    </div>
  );
}

/** A part that waits on its own call: the rest of the sub-tab shows meanwhile. */
function Part<T>({ state, children }: { state: AsyncState<T | null>; children: (data: T) => ReactNode }) {
  if (state.error) return <ErrorMessage onRetry={state.reload} />;
  if (!state.data) return <Loading />;
  return <>{children(state.data)}</>;
}

/** Resumen: the period in four numbers, what's open now, the sales chart, where the visits come from (VMAX), and the shop's reputation and times. */
function Summary({ s, ins, perf }: { s: SellerStatsView; ins: AsyncState<SellerInsights | null> | null; perf: AsyncState<SellerPerformance | null> | null }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const duration = useDuration();
  const r = s.reputation;
  const cards: { label: string; value: string; note: string }[] = [
    { label: t("st.sales"), value: String(s.sales.count), note: `${t("st.revenue", { amount: ars(s.sales.revenueArs) })} · ${t("st.cancelledNote", { count: s.cancelled })}` },
    { label: t("st.avgTicket"), value: s.sales.avgTicketArs === null ? "—" : ars(s.sales.avgTicketArs), note: t("st.avgTicketNote") },
    { label: t("st.views"), value: String(s.views), note: s.viewsSince ? t("st.viewsSince", { date: dayMonth(s.viewsSince, locale) }) : t("st.viewsNone") },
    { label: t("st.offers"), value: String(s.offers.received), note: t("st.offersAccepted", { count: s.offers.accepted }) },
  ];
  return (
    <>
      <ul className="stats-cards stats-cards-4">
        {cards.map((c) => (
          <li key={c.label}>
            <span className="muted small">{c.label}</span>
            <strong>{c.value}</strong>
            <span className="muted small">{c.note}</span>
          </li>
        ))}
      </ul>
      <p className="stats-now small">
        <strong>{t("st.nowLabel")}</strong> <Link to={sellerPath("pedidos")}>{t("st.nowOrders", { count: s.inProgress })}</Link> · {t("st.nowFavorites", { count: s.favorites })} ·{" "}
        {t("st.nowFollowers", { count: s.followers })}
      </p>

      <section aria-labelledby="st-chart">
        <h3 id="st-chart">{t(s.bucket === "week" ? "st.chartWeeks" : "st.chartMonths")}</h3>
        <SalesChart s={s} />
      </section>

      {perf && <Part state={perf}>{(d) => <Sources sources={d.sources} />}</Part>}

      <section aria-labelledby="st-rep">
        <h3 id="st-rep">{t("st.repTimes")}</h3>
        <p className="muted small">{t("st.repHint")}</p>
        <ul className="stats-rep">
          <li>
            <Stars value={r.ratingAvg} count={r.ratingCount} />
          </li>
          <li>
            {t("st.response")}: <strong>{duration(r.responseMinutes)}</strong>
          </li>
          <li>
            {t("st.cancelPct")}: <strong>{r.cancelPct === null ? "—" : `${r.cancelPct}%`}</strong>
          </li>
          <li>
            {t("st.onTimePct")}: <strong>{r.onTimePct === null ? "—" : `${r.onTimePct}%`}</strong>
          </li>
        </ul>
        {ins && (
          <Part state={ins}>
            {(d) => (
              <div className="stats-recent">
                <h4 className="small">{t("ins.recent")}</h4>
                <ul className="stats-rep">
                  <li>
                    {t("ins.response")}: <strong>{duration(d.times.responseMinutes)}</strong>
                    {d.times.responseSamples > 0 && <span className="muted small"> ({t("ins.samples", { count: d.times.responseSamples })})</span>}
                  </li>
                  <li>
                    {t("ins.dispatch")}: <strong>{duration(d.times.dispatchHours === null ? null : d.times.dispatchHours * 60)}</strong>
                    {d.times.dispatchSamples > 0 && <span className="muted small"> ({t("ins.samples", { count: d.times.dispatchSamples })})</span>}
                  </li>
                </ul>
                <p className="muted small">{t("ins.timesNote")}</p>
              </div>
            )}
          </Part>
        )}
      </section>

      <details className="muted small">
        <summary>{t("st.howWeCount")}</summary>
        {t("st.salesNote")}
      </details>
    </>
  );
}

/**
 * Cartas: first what needs a push (VMAX), then one table of the listings (BASIC: the most seen; EX and
 * up: each one's funnel instead), and the one thing a plan up would add.
 */
function Cards({ s, ins, perf, page, onPage, canFeature }: { s: SellerStatsView; ins: AsyncState<SellerInsights | null> | null; perf: AsyncState<SellerPerformance | null> | null; page: number; onPage: (p: number) => void; canFeature: boolean }) {
  const { t } = useI18n();
  const ars = useArs();
  const rotation = (d: SellerPerformance) => <p>{d.rotation.medianDays === null ? <span className="muted">{t("perf.rotationNone")}</span> : t("perf.rotationText", { days: d.rotation.medianDays, count: d.rotation.samples })}</p>;
  return (
    <>
      {perf && <Part state={perf}>{(d) => <NeedsPush d={d} canFeature={canFeature} />}</Part>}
      {ins ? (
        <Part state={ins}>
          {(d) => (
            <Funnel rows={d.funnel.rows} total={d.funnel.total} page={page} onPage={onPage}>
              {perf?.data && rotation(perf.data)}
            </Funnel>
          )}
        </Part>
      ) : (
        <section aria-labelledby="st-top">
          <h3 id="st-top">{t("st.top")}</h3>
          {s.top.length === 0 ? (
            <p className="muted">{t("st.topNone")}</p>
          ) : (
            <div className="imp-table-wrap">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th scope="col">{t("imp.product")}</th>
                    <th scope="col" className="num">{t("st.colViews")}</th>
                    <th scope="col" className="num">{t("st.colFavorites")}</th>
                    <th scope="col" className="num">{t("st.colSold")}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.top.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <Link to={`/mercado/${l.id}`}>{listingName(l)}</Link>
                        <span className="muted small">
                          {" "}
                          · {ars(l.priceArs)}
                          {l.status === "paused" ? ` · ${t("bulk.paused")}` : ""}
                        </span>
                      </td>
                      <td className="num">{l.views}</td>
                      <td className="num">{l.favorites}</td>
                      <td className="num">{l.sold}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      {!ins ? (
        <Locked text={t("st.lockedFunnel", { plan: PLAN_LABEL[planWith("insights")] })} source="vidriera:embudo" verb={t("st.lockedFunnelVerb")} />
      ) : (
        !perf && <Locked text={t("st.lockedPush", { plan: PLAN_LABEL[planWith("performance")] })} source="vidriera:empujon" verb={t("st.lockedPushVerb")} />
      )}
    </>
  );
}

/** Precios: one list of the shop's prices (EX and up: against the market's median), and who wants its cards (every plan). */
function PricesView({ s, ins }: { s: SellerStatsView; ins: AsyncState<SellerInsights | null> | null }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const store = new Map(s.pricing.map((p) => [p.id, p.storeArs]));
  return (
    <>
      {ins ? (
        <Part state={ins}>{(d) => <Prices rows={d.prices} storeOf={(id) => store.get(id) ?? null} />}</Part>
      ) : (
        <section aria-labelledby="st-pricing">
          <h3 id="st-pricing">{t("st.pricing")}</h3>
          {s.pricing.length === 0 ? (
            <p className="muted">{t("st.pricingNone")}</p>
          ) : (
            <ul className="stats-list">
              {s.pricing.map((l) => (
                <li key={l.id}>
                  <Link to={`/mercado/${l.id}`}>{listingName(l)}</Link>{" "}
                  <span className="muted small">
                    ({l.condition} · {LANGUAGE_LABEL[l.language]?.[locale] ?? l.language})
                  </span>
                  <span className="small">
                    {t("st.pricingRow", { mine: ars(l.priceArs), count: l.cheaper, min: ars(l.marketMinArs) })}
                    {l.storeArs !== null && ` ${t("st.pricingStore", { store: ars(l.storeArs) })}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section aria-labelledby="st-demand">
        <h3 id="st-demand">{t("st.demand")}</h3>
        <p className="muted small">{t("st.demandHint")}</p>
        {s.demand.length === 0 ? (
          <p className="muted">{t("st.demandNone")}</p>
        ) : (
          <ul className="stats-list">
            {s.demand.map((d) => (
              <li key={d.id}>
                <Link to={`/mercado/${d.id}`}>{listingName(d)}</Link>
                <span className="small">
                  {t("st.demandRow", { count: d.wanting, price: ars(d.priceArs) })}
                  {d.belowYou > 0 && d.highestBelowArs !== null && ` ${t("st.demandBelow", { count: d.belowYou, max: ars(d.highestBelowArs) })}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {!ins && (
        <ShowcaseOr kind="price">
          <Locked text={t("st.lockedPrices", { plan: PLAN_LABEL[planWith("insights")] })} source="vidriera:price" verb={t("st.lockedPricesVerb")} />
        </ShowcaseOr>
      )}
    </>
  );
}

/** What a plan up adds to this sub-tab: one line and one way to it. */
function Locked({ text, source, verb }: { text: string; source: `vidriera:${string}`; verb: string }) {
  return (
    <section className="plan-card stats-locked">
      <p>{text}</p>
      <UpgradeLink source={source}>{verb}</UpgradeLink>
    </section>
  );
}

/** Revenue per week or month as bars; each bar says its numbers to screen readers and on hover. */
function SalesChart({ s }: { s: SellerStatsView }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const max = Math.max(1, ...s.series.map((b) => b.revenueArs));
  if (s.series.every((b) => b.count === 0)) return <p className="muted">{t("st.chartEmpty")}</p>;
  const label = (iso: string) => (s.bucket === "week" ? dayMonth(iso, locale) : monthName(iso, locale));
  return (
    <ol className="stats-chart">
      {s.series.map((b) => {
        const text = t("st.bar", { when: label(b.start), count: b.count, amount: ars(b.revenueArs) });
        return (
          <li key={b.start} title={text}>
            <span className="sr-only">{text}</span>
            <span className="stats-bar" style={{ height: `${Math.round((b.revenueArs / max) * 100)}%` }} aria-hidden="true" />
            <span className="stats-bar-label muted" aria-hidden="true">
              {label(b.start)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
